/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:52:59]
Permissions to update outside the main branch: [false]
*/

import {
    Button,
    Form,
    ImageUpload,
    Modal,
    requiredRule,
    useErrorState,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { AxiosResponse } from 'axios';
import uploadFile from 'axios/fileUploadApi';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { DeclinedShowcase, DeclinedShowcaseAdd } from 'models/api/declinedShowcase';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Container, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import 'scss/Main.scss';
import './DeclinedShowcaseChangeModal.scss';
import { useDeclinedShowcasesWorkplaceState } from './DeclinedShowcases';

type DeclinedShowcaseChangeModalProps = {
    isOpen?: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
};

const DeclinedShowcaseChangeModalModal = ({
    isOpen = false,
    toggle,
}: DeclinedShowcaseChangeModalProps) => {
    const api = useApi();
    const { t } = useTranslation();
    const { hasRole } = useAuth();
    const [file, setFile] = useState<File>();
    const [isImageValid, setIsImageValid] = useState(true);
    const newData = useRef<DeclinedShowcase>();
    const initialItem: DeclinedShowcase = {
        id: '',
        dsc: '',
        lendingData: '',
        referralLink: '',
        logoVirtualPath: '',
        logoFileId: 0,
    };

    const { handleError } = useErrorState();

    const { changeItemMutation, addItemMutation, currentItem } =
        useDeclinedShowcasesWorkplaceState();

    const [declinedShowcase, setDeclinedShowcase] = useState<DeclinedShowcase>({
        ...initialItem,
        ...currentItem,
    });

    const [doImageUpload, setDoImageUpload] = useState<boolean>(false);

    useEffect(() => setDeclinedShowcase({ ...initialItem, ...currentItem }), [currentItem]);

    const saveChanges = async (data: DeclinedShowcase) => {
        toggle(false);
        setDoImageUpload(false);

        const formattedItem: DeclinedShowcaseAdd = {
            dsc: data.dsc,
            lendingData: data.lendingData,
            referralLink: data.referralLink,
            logoFileId: data.logoFileId,
        };

        declinedShowcase.id
            ? changeItemMutation.mutate({ ...formattedItem, id: declinedShowcase.id })
            : addItemMutation.mutate({ ...formattedItem });
    };

    const formHasRole = (): boolean =>
        declinedShowcase.id == null || declinedShowcase.id === ''
            ? hasRole('declinedShowcasesCreate')
            : hasRole('declinedShowcasesUpdate');

    return (
        <Modal
            size="lg"
            isOpen={isOpen}
            toggle={() => toggle(false)}
            scrollable
            data-testid="declined-showcase-modal"
        >
            <Form<DeclinedShowcase>
                onSubmit={(data) => {
                    setDeclinedShowcase(data);
                    if (file) {
                        newData.current = data;
                        setDoImageUpload(true);
                    } else if (declinedShowcase.id) {
                        saveChanges(data);
                    } else {
                        setIsImageValid(false);
                    }
                }}
                onInvalid={() => {
                    if (!file && !declinedShowcase.id) {
                        setIsImageValid(false);
                    }
                }}
                defaultValues={declinedShowcase}
                className="form-in-modal"
            >
                <ModalHeader toggle={() => toggle(false)}>
                    {declinedShowcase.id ? (
                        `#${declinedShowcase.id}`
                    ) : (
                        <Trans i18nKey="BackOfficeNew" />
                    )}
                </ModalHeader>
                <ModalBody className="custom-scrollbar">
                    <Container className="mb-3 px-0 d-flex justify-content-center">
                        <ImageUpload<
                            AxiosResponse<{ id: number; virtualPath: string }>,
                            AxiosErrorResponse
                        >
                            canUpload={doImageUpload}
                            valid={isImageValid}
                            onUploaded={(res) => {
                                if (newData.current) {
                                    newData.current.logoFileId = res.data.id;
                                    saveChanges(newData.current);
                                }
                            }}
                            onError={() => {
                                setDoImageUpload(false);
                                handleError('BackOfficeErrorFileUploadFailed');
                            }}
                            filePath={declinedShowcase.logoVirtualPath}
                            label={t('BackOfficeLogo')}
                            alt={t('BackOfficeLogoNotFound')}
                            onChange={(f) => {
                                setIsImageValid(true);
                                setFile(f);
                            }}
                            uploadDisabled={hasRole('declinedShowcasesUpload')}
                            query={async (file) => await uploadFile(api, 'declinedshowcases', file)}
                        />
                    </Container>
                    <ValidatedInput<DeclinedShowcase>
                        name="referralLink"
                        label={t('BackOfficeReferralLink')}
                        rules={{
                            required: requiredRule,
                        }}
                        disabled={!formHasRole()}
                    />
                    <ValidatedInput<DeclinedShowcase>
                        type="textarea"
                        name="lendingData"
                        label={t('BackOfficeLendingData')}
                        rules={{
                            required: requiredRule,
                        }}
                        disabled={!formHasRole()}
                    />
                    <ValidatedInput<DeclinedShowcase>
                        type="textarea"
                        name="dsc"
                        label={t('BackOfficeDescription')}
                        disabled={!formHasRole()}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        onClick={() => toggle(false)}
                        disabled={doImageUpload}
                    >
                        <Trans i18nKey="BackOfficeCancel" />
                    </Button>
                    <Button
                        color="success"
                        type="submit"
                        disabled={!formHasRole() || doImageUpload}
                        isPending={doImageUpload}
                    >
                        <Trans i18nKey="BackOfficeSave" />
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default DeclinedShowcaseChangeModalModal;
